"use client";

import React, { useCallback, useMemo, useRef } from "react";

import Motto from "@/components/manifesto/Motto";
import Problem from "@/components/manifesto/Problem";
import Manifesto from "@/components/manifesto/Manifesto";
import Philosofy from "@/components/manifesto/Philosofy";
import Vision from "@/components/manifesto/Vision";
import Last from "@/components/manifesto/Last";

import { cn } from "@/lib/utils";

import { PageManifestoSchema } from "@/sanity/pages/PageManifestoTemplate";
import { useInView } from "framer-motion";

export default function ScrollFollower({
  data,
}: {
  data: PageManifestoSchema;
}) {
  // I tried to pass the refs as props to the components but it didn't work
  const ref1 = useRef(null);
  const isInView1 = useInView(ref1, { amount: 0.2 });
  const ref2 = useRef(null);
  const isInView2 = useInView(ref2, { amount: 0.2 });
  const ref3 = useRef(null);
  const isInView3 = useInView(ref3, { amount: 0.2 });
  const ref4 = useRef(null);
  const isInView4 = useInView(ref4, { amount: 0.2 });
  const ref5 = useRef(null);
  const isInView5 = useInView(ref5, { amount: 0.2 });
  const ref6 = useRef(null);
  const isInView6 = useInView(ref6, { amount: 0.2 });

  const currentInView = useMemo(() => {
    if (isInView1) {
      return 1;
    }

    if (isInView2) {
      return 2;
    }

    if (isInView3) {
      return 3;
    }

    if (isInView4) {
      return 4;
    }

    if (isInView5) {
      return 5;
    }

    if (isInView6) {
      return 6;
    }
  }, [isInView1, isInView2, isInView3, isInView4, isInView5, isInView6]);

  const scrollTo = useCallback((ref: React.MutableRefObject<any>) => {
    if (!ref?.current) {
      return;
    }

    ref.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "start",
    });
  }, []);

  // Navigation items configuration
  const navItems = useMemo(() => {
    return [
      { label: data?.settings?.language === "en" ? "Our Motto" : "Unser Motto", ref: ref1 },
      { label: data?.settings?.language === "en" ? "The Problem" : "Das Problem", ref: ref2 },
      { label: data?.settings?.language === "en" ? "The counter-proposal" : "Der Gegenentwurf", ref: ref3 },
      { label: data?.settings?.language === "en" ? "Philosophy" : "Philosophie", ref: ref4 },
      { label: data?.settings?.language === "en" ? "Vision" : "Vision", ref: ref5 },
      { label: data?.settings?.language === "en" ? "Summary" : "Zusammenfassung", ref: ref6 },
    ];
  }, [data?.settings?.language]);

  return (
    <div className="w-full relative">
      {navItems.map((item, index) => {
        const itemIndex = index + 1;
        const stickyTop = 10 + (index * 4); // 10, 14, 18, 22, 26, 30
        const marginTop = index === 0 ? 20 : -(20 - ((index - 1) * 4)); // 20, -20, -16, -12, -8, -4
        const marginBottom = 20 - (index * 4); // 20, 16, 12, 8, 4, 0
        
        return (
          <div
            key={itemIndex}
            className={cn(
              "hidden lg:block w-fit ml-[3rem] text-white font-[500] px-[1.5rem] py-[1rem] border-l-[.25rem] border-l-transparent uppercase cursor-pointer mix-blend-difference sticky z-10",
              currentInView === itemIndex ? "border-l-white opacity-100" : "opacity-50"
            )}
            style={{ 
              top: `${stickyTop}rem`,
              marginTop: `${marginTop}rem`,
              marginBottom: index < navItems.length - 1 ? `${marginBottom}rem` : undefined
            }}
            onClick={() => scrollTo(item.ref)}
          >
            {item.label}
          </div>
        );
      })}
      <div ref={ref1}>
        <Motto data={data} />
      </div>
      <div ref={ref2}>
        <Problem data={data} />
      </div>
      <div ref={ref3}>
        <Manifesto data={data} />
      </div>
      <div ref={ref4}>
        <Philosofy data={data} />
      </div>
      <div ref={ref5}>
        <Vision data={data} />
      </div>
      <div ref={ref6}>
        <Last data={data} />
      </div>
    </div>
  );
}
